/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ComponentCollection,
  ItemValue,
  type PanelModel,
  QuestionCheckboxModel,
  type QuestionCompositeModel,
  QuestionPanelDynamicModel,
  QuestionRadiogroupModel,
  QuestionTextModel,
  Serializer,
} from "survey-core";
import {
  type QuestionAddedEvent,
  type SurveyCreatorModel,
} from "survey-creator-core";
import { defaultTemplate } from "shared/components/survey-js-form/questions/products/defaultTemplate";
import {
  type ProductsQuestionChoice,
  ProductsSurveyJSQuestionProperties,
  type ProductsSurveyJSQuestionProps,
  type ProductsSurveyJSQuestionValue,
  type ProductsSurveyJSQuestionValueBase,
  type ProductsSurveyJSQuestionValueSingle,
} from "shared/components/survey-js-form/questions/products/IProductsQuestion";
import {
  addQuantityQuestion,
  setQuantityValueOnSurvey,
} from "shared/components/survey-js-form/questions/products/products-question-utils/addQuantityQuestion";
import { generateIteratedQuestionName } from "shared/components/survey-js-form/utils/geIteratedQuestionName";
import { formatUsdString } from "shared/helpers/invoice/formatUsdString";
import { type SessionView } from "shared/mappers/database/session/session";

export const ProductsSurveyJSQuestionName = "products";

export const ProductsSurveyJSQuestionRegisterProperties = (
  session?: SessionView,
) => {
  Serializer.addProperty(ProductsSurveyJSQuestionName, {
    name: ProductsSurveyJSQuestionProperties.multipleChoice,
    displayName: "Multiple Choice",
    type: "boolean",
    categoryIndex: 0,
    visibleIndex: 0,
    category: "Product Question",
    default: false,
  });

  Serializer.addClass(
    "productitemvalue",
    [
      {
        name: "value",
        type: "dropdown",
        choices: session?.society?.products?.map((product) => ({
          value: product.productId,
          text: product.name,
        })),
      },
      { name: "text", type: "string", showMode: "form" },
      { name: "price", type: "number", showMode: "form" },
      { name: "showPrice", type: "boolean", showMode: "form" },
      { name: "userPriceOverride", type: "boolean", showMode: "form" },
      {
        name: "minumumQuantity",
        type: "number",
        minValue: 1,
        showMode: "form",
      },
      {
        name: "maximumQuantity",
        type: "number",
        maxValue: 999,
        showMode: "form",
      },
    ],
    undefined,
    "itemvalue",
  );
  Serializer.addProperty(ProductsSurveyJSQuestionName, {
    name: ProductsSurveyJSQuestionProperties.options,
    type: "productitemvalue[]",
    visible: true,
    categoryIndex: 0,
    visibleIndex: 1,
    category: "Product Question",
  });
};

export const ProductsSurveyJSQuestion = async ({
  session,
  creator,
}: ProductsSurveyJSQuestionProps) => {
  ProductsSurveyJSQuestionRegisterProperties(session);
  creator?.onQuestionAdded.add(
    (sender: SurveyCreatorModel, options: QuestionAddedEvent) => {
      if (options.question?.getType() == ProductsSurveyJSQuestionName) {
        options.question.name = generateIteratedQuestionName(
          ProductsSurveyJSQuestionName,
          sender,
        );
      }
    },
  );

  const ProductsSurveyJSQuestionDefinition = {
    name: ProductsSurveyJSQuestionName,
    title: "Products",
    createElements: () => {
      return [];
    },
    onInit: async () => {
      ProductsSurveyJSQuestionRegisterProperties();
    },
    onItemValuePropertyChanged(
      question: QuestionCompositeModel,
      options: {
        obj: ItemValue;
        propertyName: string;
        name: string;
        newValue: any;
      },
    ): void {
      if (
        options.propertyName === ProductsSurveyJSQuestionProperties.options &&
        options.name === "value" &&
        options.obj["text"] == options.newValue
      ) {
        const product = session?.society?.products?.find(
          (product) => product.productId === options.newValue,
        );
        options.obj["text"] = product?.name ?? "ERROR: No Product Name";
      }
      buildProductQuestionElements(question);
    },
    onValueChanged: (
      question: QuestionCompositeModel,
      name: string,
      newValue: any,
    ) => {
      const value = question.value as ProductsSurveyJSQuestionValue;
      const getQuestionByName = (name: string) =>
        question.contentPanel.getQuestionByName(name);
      if ("multipleChoiceProduct" in value) {
        const selectedProductIds = value.multipleChoiceProduct;
        const multipleChoiceProductPanel = getQuestionByName(
          "multipleChoiceProductPanel",
        ) as QuestionPanelDynamicModel;
        if (selectedProductIds.length) {
          multipleChoiceProductPanel.visible = true;
        } else {
          multipleChoiceProductPanel.visible = false;
        }
        selectedProductIds.forEach((productId, panelIdx) => {
          const productOption: ProductsQuestionChoice = question.options.find(
            (option: ProductsQuestionChoice) => option.value === productId,
          );
          const panel =
            panelIdx < multipleChoiceProductPanel.panels.length
              ? multipleChoiceProductPanel.panels[panelIdx]
              : multipleChoiceProductPanel.addPanel();

          if (productOption.minumumQuantity || productOption.maximumQuantity) {
            addQuantityQuestion({
              panel,
              productOption,
              isDropdown: true,
              question,
            });
          }
        });
      } else if ("singleChoiceProductQuestion" in value) {
        const productId = value.singleChoiceProductQuestion;
        const selectedProduct = question.options.find(
          (option: ProductsQuestionChoice) => option.value === productId,
        );

        if (
          selectedProduct.minumumQuantity ||
          selectedProduct.maximumQuantity
        ) {
          addQuantityQuestion({
            panel: question.contentPanel,
            productOption: selectedProduct,
            question,
            isDropdown: !!(
              selectedProduct?.minumumQuantity &&
              selectedProduct?.maximumQuantity
            ),
          });
        } else {
          question.contentPanel.removeElement(
            question.contentPanel.getQuestionByName("quantity"),
          );
        }
      }

      if (name == "multipleChoiceProductPanel" && newValue.quantity) {
        setQuantityValueOnSurvey({
          question,
          newValue: newValue.quantity,
          productId: newValue.productId,
        });
      } else if (name == "quantity") {
        setQuantityValueOnSurvey({
          question,
          newValue,
          productId: question.value.productId,
        });
      }
    },

    onValueChanging: (
      question: QuestionCompositeModel,
      name: string,
      newValue: any,
    ) => {
      if (name === "multipleChoiceProduct") {
        const selectedProducts = newValue as number[];

        const dynamicPanel = <QuestionPanelDynamicModel>(
          question.contentPanel.getQuestionByName("multipleChoiceProductPanel")
        );
        dynamicPanel.visible = false;
        const existingValues = dynamicPanel?.value || [];
        const updatedValues = selectedProducts.map((productId) => {
          const productOption: ProductsQuestionChoice = question.options.find(
            (option: ProductsQuestionChoice) => option.value === productId,
          );
          const existingProduct = existingValues.find(
            (value: ProductsSurveyJSQuestionValueBase) =>
              value.productId === productId,
          );
          return {
            value: productId,
            productId: productId,
            price: productOption.price,
            quantity:
              existingProduct?.quantity ?? productOption.minumumQuantity ?? 1,
            invoiceItemId: existingProduct?.invoiceItemId,
          };
        });

        if (dynamicPanel) {
          dynamicPanel.value = updatedValues;
        }
      } else if (name === "singleChoiceProductQuestion") {
        const productItemValue = question.options.find(
          (option: ProductsQuestionChoice) => option.value === newValue,
        );
        question.setValue(
          "quantity",
          productItemValue.minumumQuantity,
          true,
          true,
        );
        question.setValue("productId", productItemValue.value, true, true);
        question.setValue("price", productItemValue.price, true, true);
      }
      return newValue;
    },
    onAfterRender: (question: QuestionCompositeModel) => {
      const survey: any = question?.survey;

      if (question.getType() !== "products" || !survey?.formData) {
        return;
      }

      applyReadOnlyLogicToMultipleChoice(question, survey.formData);

      if (survey.formData[question.name]?.invoiceItemId) {
        question.readOnly = true;
      }

      const existingProducts =
        survey.formData[question.name]?.multipleChoiceProductPanel ||
        survey.formData[question.name];

      if (Array.isArray(existingProducts)) {
        existingProducts.forEach((product: any, index: number) => {
          if (product?.quantity !== undefined) {
            question.setValue(
              `multipleChoiceProductPanel[${index}].quantity`,
              product.quantity,
              true,
              true,
            );
          }
        });
      } else if (existingProducts?.quantity !== undefined) {
        question.setValue("quantity", existingProducts.quantity, true, true);
      }
    },
    onLoaded: (question: QuestionCompositeModel) => {
      buildProductQuestionElements(question);
    },

    elementsJSON: defaultTemplate,
  };

  const buildProductQuestionChoiceText = (option: ProductsQuestionChoice) => {
    const product = session?.society?.products?.find(
      (product) => product.productId === option.value,
    );
    const formattedPrice = formatUsdString(option.price ?? product?.price ?? 0);
    if (option.showPrice) {
      return `${
        option.text ?? product?.name ?? "ERROR: No Product Name OR Description"
      } - ${formattedPrice}`;
    }
    return (
      option.text ?? product?.name ?? "ERROR: No Product Name OR Description"
    );
  };

  const buildProductQuestionElements = (question: QuestionCompositeModel) => {
    const panel = <PanelModel>question["contentPanel"];
    if (!panel) {
      return;
    }
    for (const element of panel.elements) {
      panel.removeElement(element);
    }
    buildProductQuestionElementsMultipleChoice(question);
    buildProductQuestionElementsSingleChoice(question);
  };

  const buildProductQuestionElementsMultipleChoice = (
    question: QuestionCompositeModel,
  ) => {
    const panel = <PanelModel>question["contentPanel"];
    if (!panel) {
      return;
    }

    const { multipleChoice } = question;
    const options = question.options as ProductsQuestionChoice[];

    if (multipleChoice === true) {
      const multipleChoiceProductQuestion = new QuestionCheckboxModel(
        "multipleChoiceProduct",
      );
      multipleChoiceProductQuestion.title = "";
      multipleChoiceProductQuestion.titleLocation = "hidden";
      multipleChoiceProductQuestion.valueName = "multipleChoiceProduct";
      multipleChoiceProductQuestion.isEditableTemplateElement = false;
      multipleChoiceProductQuestion.isInteractiveDesignElement = false;
      multipleChoiceProductQuestion.isContentElement = true;

      multipleChoiceProductQuestion.choices = options?.map(
        (option: ProductsQuestionChoice) => {
          const itemValue = new ItemValue(
            option.value, // index + 1,
            buildProductQuestionChoiceText(option),
          );
          if (option.visibleIf && option.visibleIf !== "") {
            itemValue.visibleIf = option.visibleIf;
          }
          if (option.enableIf && option.enableIf !== "") {
            itemValue.enableIf = option.enableIf;
          }
          setQuantityValueOnSurvey({
            question,
            newValue: option.minumumQuantity ?? 1,
            productId: option.value,
          });
          return itemValue;
        },
      );

      panel.addQuestion(multipleChoiceProductQuestion);

      // Add a hidden dynamic panel for storing product details
      const productsPanel = new QuestionPanelDynamicModel(
        "multipleChoiceProductPanel",
      );
      productsPanel.titleLocation = "hidden";
      productsPanel.isEditableTemplateElement = false;
      productsPanel.isInteractiveDesignElement = false;
      productsPanel.isContentElement = true;
      productsPanel.visible = false;
      productsPanel.clearIfInvisible = "none";
      productsPanel.allowAddPanel = false;
      productsPanel.allowRemovePanel = false;

      ["productId", "price", "invoiceId", "invoiceItemId"].forEach((name) => {
        const hiddenTextQuestion = new QuestionTextModel(name);
        hiddenTextQuestion.visible = false;
        hiddenTextQuestion.clearIfInvisible = "none";
        hiddenTextQuestion.isEditableTemplateElement = false;
        hiddenTextQuestion.isInteractiveDesignElement = false;
        hiddenTextQuestion.isContentElement = true;
        productsPanel.template.addElement(hiddenTextQuestion);
      });

      panel.addElement(productsPanel);
    }
  };

  const buildProductQuestionElementsSingleChoice = (
    question: QuestionCompositeModel,
  ) => {
    const panel = question.contentPanel;
    if (!panel) {
      return;
    }

    const { multipleChoice } = question;
    const options = question.options as ProductsQuestionChoice[];

    if (multipleChoice !== true) {
      const singleChoiceProductQuestion = new QuestionRadiogroupModel(
        "singleChoiceProductQuestion",
      );
      singleChoiceProductQuestion.titleLocation = "hidden";
      singleChoiceProductQuestion.isEditableTemplateElement = false;
      singleChoiceProductQuestion.isInteractiveDesignElement = false;
      singleChoiceProductQuestion.isContentElement = true;

      const questionValue = <ProductsSurveyJSQuestionValueSingle | undefined>(
        question?.value?.[question.name]
      );

      singleChoiceProductQuestion.choices = options?.map(
        (option: ProductsQuestionChoice, index: number) => {
          if (!session?.adminMode && questionValue?.invoiceItemId) {
            singleChoiceProductQuestion.readOnly = true;
          }
          const itemValue = new ItemValue(
            option.value,
            buildProductQuestionChoiceText(option),
          );
          if (option.visibleIf && option.visibleIf !== "") {
            itemValue.visibleIf = option.visibleIf;
          }
          if (option.enableIf && option.enableIf !== "") {
            itemValue.enableIf = option.enableIf;
          }
          setQuantityValueOnSurvey({
            question,
            newValue: option.minumumQuantity ?? 1,
            productId: option.value,
          });
          return itemValue;
        },
      );

      if (
        !panel.elements.find(
          (element) => element.name == "singleChoiceProductQuestion",
        )
      ) {
        panel.addElement(singleChoiceProductQuestion);
      }

      ["productId", "price", "invoiceId", "invoiceItemId"].forEach((name) => {
        if (!panel.elements.find((element) => element.name == name)) {
          const questionTextModel = new QuestionTextModel(name);
          questionTextModel.value =
            questionValue?.[name as keyof ProductsSurveyJSQuestionValueSingle];
          questionTextModel.clearIfInvisible = "none";
          questionTextModel.visible = false;
          questionTextModel.isEditableTemplateElement = false;
          questionTextModel.isInteractiveDesignElement = false;
          questionTextModel.isContentElement = true;
          panel.addElement(questionTextModel);
        }
      });
    }
  };

  // Apply read-only logic based on invoice data
  const applyReadOnlyLogicToMultipleChoice = (
    question: QuestionCompositeModel,
    formData: any,
  ) => {
    const panel = <PanelModel>question["contentPanel"];
    if (!panel) {
      return;
    }

    const { multipleChoice } = question;
    if (multipleChoice !== true) {
      return;
    }

    const multipleChoiceProductQuestion = panel.getQuestionByName(
      "multipleChoiceProduct",
    ) as QuestionCheckboxModel;

    if (!multipleChoiceProductQuestion) {
      return;
    }

    const questionData = formData?.[question.name];
    const multipleChoiceProductPanel = questionData?.multipleChoiceProductPanel;
    if (
      !multipleChoiceProductPanel ||
      !Array.isArray(multipleChoiceProductPanel)
    ) {
      return;
    }

    const productIdsWithInvoice = multipleChoiceProductPanel
      .filter((item: any) => item.invoiceItemId)
      .map((item: any) => item.productId);

    if (productIdsWithInvoice.length === 0) {
      return;
    }

    multipleChoiceProductQuestion.choices.forEach((choice) => {
      if (productIdsWithInvoice.includes(choice.value)) {
        choice.readOnly = true;
        choice.enableIf = "false";
      }
    });
    console.log(multipleChoiceProductQuestion.choices);
  };

  if (
    !ComponentCollection.Instance.getCustomQuestionByName(
      ProductsSurveyJSQuestionName,
    )
  ) {
    ComponentCollection.Instance.add(ProductsSurveyJSQuestionDefinition);
  }
};
